/**
 * useUserRoles — React Query hook for the current user's role state.
 * Per CHAP-63.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from '../lib/api.js';
import type { UserMe, Role, CustomerTier } from '../../shared/types.js';

export interface UserRolesResult {
  primaryRole: Role;
  allowedRoles: Role[];
  tier: CustomerTier | null;
  committees: string[];
  isMultiRole: boolean;
  isLoading: boolean;
  error: Error | null;
  user: UserMe['user'] | null;
}

const EMPTY: UserRolesResult = {
  primaryRole: 'customer',
  allowedRoles: ['customer'],
  tier: null,
  committees: [],
  isMultiRole: false,
  isLoading: false,
  error: null,
  user: null,
};

function useMeQuery(): UseQueryResult<UserMe, Error> {
  return useQuery<UserMe, Error>({
    queryKey: ['me'],
    queryFn: () => apiGet<UserMe>('/api/me'),
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => {
      // Don't retry on 401 — user is just signed out
      const msg = String(err);
      if (msg.includes('401')) return false;
      return count < 1;
    },
  });
}

export function useUserRoles(): UserRolesResult {
  const { data, isLoading, error } = useMeQuery();
  if (!data) {
    return { ...EMPTY, isLoading };
  }
  return {
    primaryRole: data.role,
    allowedRoles: data.allowedRoles,
    tier: data.tier,
    committees: data.committees,
    isMultiRole: data.allowedRoles.length > 1,
    isLoading,
    error: error ?? null,
    user: data.user,
  };
}
